/**
 * GraphQL queries and hooks — project pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql, type DocumentNode } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useMemo } from 'react';
import type {
  Project,
  ProjectConnection,
  ProjectFilter,
  ProjectMilestone,
  ProjectOutcome,
  ServiceAssignment,
} from '../types/project';

export const PROJECT_FIELDS = gql`
  fragment ProjectFields on Project {
    entityId
    entityType
    name
    description
    status
    ownerDivision
    ownerTeamId
    startDate
    targetEndDate
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const LIST_PROJECTS = gql`
  ${PROJECT_FIELDS}
  query ListProjects($filter: ProjectFilter, $first: Int, $after: String) {
    projects(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...ProjectFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_PROJECT = gql`
  ${PROJECT_FIELDS}
  query GetProject($id: ID!) {
    project(id: $id) {
      ...ProjectFields
    }
  }
`;

/** Backs the Projects-list milestone filter's dropdown options (issue #646). */
export const LIST_PROJECT_MILESTONE_NAMES = gql`
  query ListProjectMilestoneNames {
    projectMilestoneNames
  }
`;

/** Backs the Projects-list outcome filter's dropdown options (issue #646). */
export const LIST_PROJECT_OUTCOME_NAMES = gql`
  query ListProjectOutcomeNames {
    projectOutcomeNames
  }
`;

export const LIST_PROJECT_MILESTONES = gql`
  query ListProjectMilestones($projectId: ID!) {
    projectMilestones(projectId: $projectId) {
      entityId
      entityType
      projectId
      name
      sequence
      targetDate
      status
      description
      _schemaVersion
      _createdAt
      _createdBy
      _updatedAt
      _updatedBy
      _deletedAt
    }
  }
`;

export const LIST_PROJECT_OUTCOMES = gql`
  query ListProjectOutcomes($projectId: ID!) {
    projectOutcomes(projectId: $projectId) {
      entityId
      entityType
      projectId
      name
      description
      displayOrder
      status
      _schemaVersion
      _createdAt
      _createdBy
      _updatedAt
      _updatedBy
      _deletedAt
    }
  }
`;

export const LIST_SERVICE_ASSIGNMENTS = gql`
  query ListServiceAssignments($serviceId: ID!) {
    serviceAssignments(serviceId: $serviceId) {
      serviceId
      milestoneId
      projectId
      outcomeId
      assignmentStatus
      assignedBy
      assignedAt
      statusNote
      _createdAt
      _updatedAt
    }
  }
`;

export const PROJECT_STATUS_FOR_OWNED_SERVICES = gql`
  query ProjectStatusForOwnedServices {
    projectStatusForOwnedServices {
      serviceId
      milestoneId
      projectId
      outcomeId
      assignmentStatus
      assignedBy
      assignedAt
      statusNote
      _createdAt
      _updatedAt
    }
  }
`;

export function useProjects(filter?: ProjectFilter): QueryResult<{ projects: ProjectConnection }> {
  return useQuery(LIST_PROJECTS, {
    variables: { filter, first: 100 },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/** Distinct milestone names for the Projects-list milestone filter (issue #646). */
export function useProjectMilestoneNames(): QueryResult<{ projectMilestoneNames: string[] }> {
  return useQuery(LIST_PROJECT_MILESTONE_NAMES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/** Distinct outcome names for the Projects-list outcome filter (issue #646). */
export function useProjectOutcomeNames(): QueryResult<{ projectOutcomeNames: string[] }> {
  return useQuery(LIST_PROJECT_OUTCOME_NAMES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export function useProject(id: string): QueryResult<{ project: Project | null }> {
  return useQuery(GET_PROJECT, {
    variables: { id },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !id,
  });
}

export function useProjectMilestones(
  projectId: string
): QueryResult<{ projectMilestones: ProjectMilestone[] }> {
  return useQuery(LIST_PROJECT_MILESTONES, {
    variables: { projectId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !projectId,
  });
}

export function useProjectOutcomes(
  projectId: string
): QueryResult<{ projectOutcomes: ProjectOutcome[] }> {
  return useQuery(LIST_PROJECT_OUTCOMES, {
    variables: { projectId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !projectId,
  });
}

export function useServiceAssignments(
  serviceId: string
): QueryResult<{ serviceAssignments: ServiceAssignment[] }> {
  return useQuery(LIST_SERVICE_ASSIGNMENTS, {
    variables: { serviceId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !serviceId,
  });
}

export function useProjectStatusForOwnedServices(): QueryResult<{
  projectStatusForOwnedServices: ServiceAssignment[];
}> {
  return useQuery(PROJECT_STATUS_FOR_OWNED_SERVICES, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

const ASSIGNMENT_FIELDS = `
  serviceId
  milestoneId
  projectId
  outcomeId
  assignmentStatus
  assignedBy
  assignedAt
  statusNote
  _createdAt
  _updatedAt
`;

const EMPTY_PROJECT_ASSIGNMENTS_QUERY = gql`
  query ProjectAssignmentsEmpty {
    __typename
  }
`;

export function buildProjectAssignmentsQuery(milestoneIds: string[]): DocumentNode {
  if (milestoneIds.length === 0) return EMPTY_PROJECT_ASSIGNMENTS_QUERY;
  const params = milestoneIds.map((_, i) => `$id${i}: ID!`).join(', ');
  const aliases = milestoneIds
    .map((_, i) => `m${i}: milestoneAssignments(milestoneId: $id${i}) { ${ASSIGNMENT_FIELDS} }`)
    .join('\n    ');
  return gql`
    query ProjectAssignments(${params}) {
      ${aliases}
    }
  `;
}

type QueryError = QueryResult<unknown>['error'];

export interface ProjectAssignmentsResult {
  assignments: Record<string, ServiceAssignment[]>;
  loading: boolean;
  error: QueryError;
  refetch: () => void;
}

export function useProjectAssignments(milestoneIds: string[]): ProjectAssignmentsResult {
  const idKey = milestoneIds.join(',');

  // idKey is a stable serialization of milestoneIds — used to memoize the dynamic query doc
  const query = useMemo(() => buildProjectAssignmentsQuery(milestoneIds), [idKey]);

  const variables = useMemo(
    () => Object.fromEntries(milestoneIds.map((id, i) => [`id${i}`, id])),
    [idKey]
  );

  const { data, loading, error, refetch } = useQuery<Record<string, ServiceAssignment[]>>(query, {
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: milestoneIds.length === 0,
  });

  const assignments = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(milestoneIds.map((id, i) => [id, data[`m${i}`] ?? []]));
  }, [data, idKey]);

  return {
    assignments,
    loading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}
